import { createSlice } from '@reduxjs/toolkit';
import {
  addInvoiceLogMessage,
  deleteInvoiceLogMessage,
  downloadInvoiceFile,
  downloadInvoicesExcel,
  facilityModeToggle,
  getFacilityInvoiceSettingsAPI,
  getInvoiceBreakdownData,
  getInvoiceAnalyzeLogs,
  getInvoiceFormData,
  getInvoiceApproverOptions,
  getInvoiceGlItems,
  getInvoiceItemsFiles,
  getInvoiceLogData,
  getInvoiceStatements,
  getInvoiceViewersList,
  getInvoiceViewersPerFacility,
  getEditFacilities,
  getPendingInvoicesData,
  getGroupedInvoicesData,
  getSingleInvoice,
  getInvoiceGLsData,
  postAddInvoiceFile,
  postInvoiceGLItemsForm,
  postDeleteFacilityInvoiceApprovers,
  getCompleteInvoicesData,
  removeGlItems,
  updateFacilitiesGlConsolidation,
  updateInvoiceField,
  updateInvoiceItems,
  uploadManualInvoices,
  viewInvoiceFile,
  postInvoiceApprovers,
  getInvoiceApprovers,
  postFacilityInvoiceApprovers,
  getFacilityManagersApi,
  getFacilityApprovalAssignments,
  processInvoiceApproval,
  requestReview,
  bulkBookInvoices,
  removeInvoiceFromQue,
  getParsingInvoices,
  stopParsingInvoice,
  getInvoiceChatUsers,
  addInvoiceChatUser,
  deleteInvoiceChatUser,
  setEmailChatUpdates,
  parseGLItems,
  getBookingTypeSubmitSchedules,
  getInvoiceOCRStatus,
  postInvoiceForm,
  getDuplicateInvoices,
  getRelatedInvoices,
} from '../../api/invoicing';
import { getVendorsData, getInvoiceBookingTypes, postDeleteVendors } from '../../api/invoicing/invoicingVendors';
import {
  isUserPendingInvoice,
  isAwaitingSubmissionInvoice,
  isInvoiceInQueue,
  isRejectedInvoice,
} from '../../utils/helpers/invoicingHelper';
// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  data: {
    manualInvoicing: [],
    pendingInvoicing: [],
    pendingFilterInvoicing: [],
    invoicesAwaitingSubmission: [],
    invoicesInQueue: [],
    rejectedInvoices: [],
    viewedInvoiceIds: [],
    previousInvoicing: [],
    facilityManagers: [],
    facilityInvoicingApprovers: [],
    invoiceApproverOptions: [],
    InvoiceViewersPerFacility: [],
    invoicesLatestChat: {},
    invoicingGLs: [],
    invoicingGLCategories: [],
    editableGLFacilities: [],
    invoicesInParsingProgress: [],
    bookingTypeSubmitSchedules: [],
    statements: {
      vendors: [],
      statements: [],
    },
    invoiceData: {
      categories: [],
      manualCategories: [],
      subcategories: [],
      manualSubcategories: [],
      vendors: [],
      manualVendors: [],
      facilities: [],
      manualFacilities: [],
    },
    invoicingVendors: {
      vendors: [],
      editableVendorBookingTypes: [],
    },
    facilitiesWithEditInvoicePermission: [],
    invoiceLogData: [],
    invoiceAnalyzeLogs: [],
    invoiceItemData: [],
    invoiceGLItems: [],
    invoicePhysicalFile: null,
    invoicePhysicalAttachment: null,
    invoiceFiles: [],
    invoiceApprovers: [],
    invoiceViewers: [],
    duplicateInvoices: { invoiceId: 0, duplicates: [] },
    relatedInvoices: { invoiceId: 0, related: [] },
    invoiceFacilitySettings: [],
    invoiceChatContacts: [],
    invoiceChatUsers: {},
    emailChatUpdates: false,
  },
  orderDateRange: {
    startDate: null,
    endDate: null,
  },
  pendingCount: 0,
};

const slice = createSlice({
  name: 'invoicing',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    clearItemData(state) {
      state.data.previousInvoicing = [];
    },
    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    updateInvoiceFieldSuccess(state, action) {
      state.isLoading = false;
      state.data.invoiceItemData.invoice = action.payload;
    },
    updateInvoiceArraysWithFilesInRedux(state, action) {
      const updateMap = action.payload.reduce((acc, val) => {
        acc[val.invoiceId] = val.pageCount;
        return acc;
      }, {});
      const updateInvoiceArray = (invoiceArray) =>
        invoiceArray?.map((invoice) =>
          updateMap[invoice.id] ? { ...invoice, hasMainFile: true, mainFileNumPages: updateMap[invoice.id] } : invoice
        );
      state.data.manualInvoicing = updateInvoiceArray(state.data.manualInvoicing);
      state.data.pendingInvoicing = updateInvoiceArray(state.data.pendingInvoicing);
      state.data.pendingFilterInvoicing = updateInvoiceArray(state.data.pendingFilterInvoicing);
      state.data.invoicesAwaitingSubmission = updateInvoiceArray(state.data.invoicesAwaitingSubmission);
      state.data.invoicesInQueue = updateInvoiceArray(state.data.invoicesInQueue);
      state.data.rejectedInvoices = updateInvoiceArray(state.data.rejectedInvoices);
    },
    postInvoiceGLItemsFormSuccess(state, action) {
      state.isLoading = false;
      state.data.invoiceGLItems = action.payload;
    },
    removeGlItemsSuccess(state, action) {
      state.isLoading = false;
      state.data.invoiceGLItems = [];
    },
    facilityModeToggleSuccess(state, action) {
      state.isLoading = false;
      state.data.invoiceItemData = action.payload;
      state.data.invoiceGLItems = [];
    },

    getPendingInvoicingSuccess(state, action) {
      state.isLoading = false;

      const sortedByDates = action.payload.sort((a, b) => new Date(b.date) - new Date(a.date));

      const sortedByFMA = sortedByDates.sort((a, b) => {
        const aFma = a.facilityManagerAssignments.find((fma) => fma.currentUser);
        const bFma = b.facilityManagerAssignments.find((fma) => fma.currentUser);
        return (bFma ? 1 : 0) - (aFma ? 1 : 0); // if bFma is true, push b up
      });

      state.data.pendingInvoicing = sortedByFMA;
    },
    getInvoicesSuccess(state, action) {
      const { data, editInProgress, invoiceIds, user } = action.payload;    
      const isManual = editInProgress === true || editInProgress === null;
      const isPending = editInProgress === false || editInProgress === null;
      if (invoiceIds?.length > 0) {
        const deletedInvoices = invoiceIds.filter((invId) => !data.map((x) => x.id).includes(invId));
        data.forEach((updatedInvoice) => {
          // Manual
          if (isManual) {
            const index = state.data.manualInvoicing.findIndex((x) => x.id === updatedInvoice.id);
            // existing invoice
            if (index !== -1) {
              // invoice removed from "new invoices" tab
              if (updatedInvoice.editInProgress !== true)
                state.data.manualInvoicing = state.data.manualInvoicing.filter((x) => x.id !== updatedInvoice.id);
              else state.data.manualInvoicing[index] = updatedInvoice;
            } else if (updatedInvoice.editInProgress === true) {
              state.data.manualInvoicing.push(updatedInvoice); // new invoice added
            }
          }

          // Pending
          if (isPending) {
            const index = state.data.pendingInvoicing.findIndex((x) => x.id === updatedInvoice.id);
            // existing invoice
            if (index !== -1) {
              if (updatedInvoice.editInProgress === true)
                state.data.pendingInvoicing = state.data.pendingInvoicing.filter((x) => x.id !== updatedInvoice.id);
              else state.data.pendingInvoicing[index] = updatedInvoice;
            } else if (updatedInvoice.editInProgress !== true) {
              state.data.pendingInvoicing.push(updatedInvoice); // new invoice added
            }
          }
        });

        if (isManual) {
          state.data.manualInvoicing = state.data.manualInvoicing.filter((x) => !deletedInvoices.includes(x.id)); // deleted invoice
        }

        if (isPending) {
          state.data.pendingInvoicing = state.data.pendingInvoicing.filter((x) => !deletedInvoices.includes(x.id)); // deleted invoice
          state.data.pendingFilterInvoicing = state.data.pendingInvoicing.filter((x) => isUserPendingInvoice(x, user.email));
          state.data.invoicesAwaitingSubmission = state.data.pendingInvoicing.filter((x) => isAwaitingSubmissionInvoice(x));
          state.data.invoicesInQueue = state.data.pendingInvoicing.filter((x) => isInvoiceInQueue(x));
          state.data.rejectedInvoices = state.data.pendingInvoicing.filter((x) => isRejectedInvoice(x));
        }
      } else {
        if (isManual) {
          state.data.manualInvoicing = data.filter((x) => x.editInProgress === true);
        }

        if (isPending) {
          const pending = data.filter((x) => x.editInProgress !== true);
          state.data.pendingInvoicing = pending;
          state.data.pendingFilterInvoicing = pending.filter((x) => isUserPendingInvoice(x, user.email));
          state.data.invoicesAwaitingSubmission = pending.filter((x) => isAwaitingSubmissionInvoice(x));
          state.data.invoicesInQueue = pending.filter((x) => isInvoiceInQueue(x));
          state.data.rejectedInvoices = pending.filter((x) => isRejectedInvoice(x));
        }
      }
    },

    getCompleteInvoicesSuccess(state, action) {
      state.isLoading = false;
      state.data.previousInvoicing = action.payload;
    },
    updatePreviousInvoicingWithNewBookingVendorStatus(state, action) {
      state.data.previousInvoicing = state.data.previousInvoicing?.map((invoice) =>
        invoice.id === action.payload.invoiceId ? { ...invoice, bookingVendorStatus: action.payload.status } : invoice
      );
    },
    getParsingInvoicesSuccess(state, action) {
      state.isLoading = false;
      state.data.invoicesInParsingProgress = action.payload;
    },
    stopParsingInvoiceSuccess(state, action) {
      state.isLoading = false;
      state.data.invoicesInParsingProgress = action.payload;
    },
    uploadManualInvoicesSuccess(state, action) {
      state.isLoading = false;
      //update the invoices in parsing progress to check the status of the uploaded invoices
      state.data.invoicesInParsingProgress = action.payload;
    },
    removeFromPending(state, action) {
      state.isLoading = false;
      state.data.pendingInvoicing = state.data.pendingInvoicing.filter((invoice) => invoice.id !== action.payload);
    },

    getStatementsSuccess(state, action) {
      state.isLoading = false;
      state.data.statements = action.payload;
    },
    getFacilityManagersSuccess(state, action) {
      state.isLoading = false;
      state.data.facilityManagers = action.payload;
    },

    getPreviousInvoicingSuccess(state, action) {
      state.isLoading = false;
      const sortedByDates = action.payload.sort((a, b) => new Date(b.date) - new Date(a.date));

      state.data.previousInvoicing = sortedByDates;
    },

    updateOrderDateRange(state, action) {
      state.isLoading = false;
      state.orderDateRange = action.payload;
    },
    getInvoiceItemsFilesSuccess(state, action) {
      state.isLoading = false;
      state.data.invoiceFiles = action.payload;
    },

    getInvoiceDataSuccess(state, action) {
      state.data.invoiceData.categories = action.payload.categories;
      state.data.invoiceData.subcategories = action.payload.subcategories;
      state.data.invoiceData.vendors = action.payload.vendors;
      state.data.invoiceData.facilities = action.payload.facilities;
    },
    getManualInvoiceDataSuccess(state, action) {
      state.data.invoiceData.manualCategories = action.payload.manualCategories;
      state.data.invoiceData.manualSubcategories = action.payload.manualSubcategories;
      state.data.invoiceData.manualFacilities = action.payload.manualFacilities;
      state.data.invoiceData.manualVendors = action.payload.manualVendors;
    },

    getEditInvoiceFacilitiesSuccess(state, action) {
      state.data.facilitiesWithEditInvoicePermission = action.payload;
    },

    getInvoiceGlsSuccess(state, action) {
      state.isLoading = false;
      state.data.invoiceGLItems = action.payload;
    },

    getInvoiceSuccess(state, action) {
      state.isLoading = false;
      state.data.invoiceItemData.invoice = action.payload;
    },

    getInvoiceBreakdownSuccess(state, action) {
      state.data.invoiceItemData = action.payload.invoiceItems;
      state.data.invoiceFiles = action.payload.invoiceFiles;
      state.data.invoiceLogData = action.payload.invoiceLog;
      state.data.invoiceGLItems = action.payload.invoiceGlItems;
      state.data.invoiceViewers = action.payload.invoiceViewers;
      state.data.invoiceApprovers = action.payload.invoiceApprovers;
      state.data.emailChatUpdates = action.payload.invoiceItems.invoice.emailChatUpdates;
      state.data.invoiceChatContacts = action.payload.invoiceChatContacts;
      state.isLoading = false;
    },
    getInvoiceAnalyzeLogsSuccess(state, action) {
      state.data.invoiceAnalyzeLogs = action.payload;
    },
    getDuplicateInvoicesSuccess(state, action) {
      state.isLoading = false;
      state.data.duplicateInvoices = action.payload;
    },
    getRelatedInvoicesSuccess(state, action) {
      state.isLoading = false;
      state.data.relatedInvoices = action.payload;
    },
    getInvoiceFileSuccess(state, action) {
      state.isLoading = false;
      state.data.invoicePhysicalFile = action.payload;
    },  

    getFacilityApprovalAssignmentsSuccess(state, action) {
      state.isLoading = false;
      state.data.facilityInvoicingApprovers = action.payload;
    },

    getInvoiceApproverOptionsSuccess(state, action) {
      state.isLoading = false;
      state.data.invoiceApproverOptions = action.payload;
    },

    renderInvoiceFileSuccess(state, action) {
      state.isLoading = false;
      const blobUrl = URL.createObjectURL(
        new Blob([action.payload.data], { type: action.payload.headers['content-type'] })
      );
      state.data.invoicePhysicalFile = blobUrl;
    },

    renderInvoiceFileFail(state, action) {
      state.isLoading = false;
      state.data.invoicePhysicalFile = null;
    },

    getInvoiceAttachmentsSuccess(state, action) {
      state.isLoading = false;
      state.data.invoicePhysicalAttachment = action.payload;
    },

    renderInvoiceAttachmentsSuccess(state, action) {
      const blobUrl = URL.createObjectURL(
        new Blob([action.payload.data], { type: action.payload.headers['content-type'] })
      );
      state.data.invoicePhysicalAttachment = blobUrl;
      state.isLoading = false;
    },
    postAddInvoiceFileSuccess(state, action) {
      state.data.invoiceFiles = action.payload;
      state.isLoading = false;
    },
    getGLsSuccess(state, action) {
      state.isLoading = false;
      const { categories, subcategories, editableFacilities } = action.payload;
      state.data.invoicingGLs = [...subcategories];
      state.data.invoicingGLCategories = [...categories];
      state.data.editableGLFacilities = [...editableFacilities];
    },
    addInvoiceLogSuccess(state, action) {
      state.isLoading = false;
      state.data.invoiceLogData = action.payload;
    },
    getInvoiceLogSuccess(state, action) {
      state.isLoading = false;
      state.data.invoiceLogData = action.payload;
    },

    deleteInvoiceLogMessageSuccess(state, action) {
      state.isLoading = false;
      state.data.invoiceLogData = action.payload;
    },

    getInvoiceViewersListSuccess(state, action) {
      state.data.invoiceViewers = action.payload;
    },

    getFacilityInvoiceSettingsSuccess(state, action) {
      state.data.invoiceFacilitySettings = action.payload;
    },
    getInvoiceViewersPerFacilitySuccess(state, action) {
      state.data.InvoiceViewersPerFacility = action.payload;
    },

    getInvoiceApproversSuccess(state, action) {
      state.isLoading = false;
      state.data.invoiceApprovers = action.payload;
    },
    updateFacilityInvoiceSettingsSuccess(state, action) {
      state.data.invoiceFacilitySettings = action.payload;
    },
    postFacilityInvoiceApproversSuccess(state, action) {
      state.isLoading = false;
      state.data.facilityInvoicingApprovers = action.payload;
    },
    postDeleteFacilityInvoiceApproversSuccess(state, action) {
      state.isLoading = false;
      state.data.facilityInvoicingApprovers = action.payload;
    },
    getPendingInvoicesCountSuccess(state, action) {
      const { pendingCount } = action.payload;
      state.isLoading = false;
      state.pendingCount = pendingCount;
    },
    addViewedInvoiceIdSuccess: (state, action) => {
      const invoiceId = action.payload;
      const viewed = [...(state.data.viewedInvoiceIds ?? [])]; // now viewed is a regular array
      if (!viewed.includes(Number(invoiceId))) {
        viewed.push(Number(invoiceId));
        state.data.viewedInvoiceIds = viewed;
      }
    },
    clearViewedInvoiceIdsSuccess: (state) => {
      state.data.viewedInvoiceIds = [];
    },
    setInvoiceChatUsers: (state, action) => {
      state.isLoading = false;
      state.data.invoiceChatUsers = action.payload;
    },
    setEmailChatUpdatesVariable: (state, action) => {
      state.isLoading = false;
      state.data.emailChatUpdates = action.payload;
    },
    getBookingTypeSubmitSchedulesSuccess: (state, action) => {
      state.isLoading = false;
      state.data.bookingTypeSubmitSchedules = action.payload;
    },
    getVendorsSuccess(state, action) {
      state.isLoading = false;
      const { vendors, vendorIds } = action.payload;

      if (!vendorIds?.length) {
        // Full refresh
        state.data.invoicingVendors.vendors = vendors;
      } else {
        // Partial update — replace only the vendors that were fetched
        const returnedIds = new Set(vendors.map((v) => v.id));
        state.data.invoicingVendors.vendors = [
          ...state.data.invoicingVendors.vendors.filter((v) => !returnedIds.has(v.id)),
          ...vendors,
        ];
      }
    },
    removeVendorsSuccess(state, action) {
      state.isLoading = false;
      const vendorIds = action.payload;
      state.data.invoicingVendors.vendors = state.data.invoicingVendors.vendors.filter(
        (v) => !vendorIds.includes(v.id)
      );
    },
    getBookingTypesSuccess(state, action) {
      state.isLoading = false;
      state.data.invoicingVendors.editableVendorBookingTypes = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;
// ----------------------------------------------------------------------

export function patchInvoiceItems(invoiceId, data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await updateInvoiceItems(invoiceId, data);
      return response;
    } catch (error) {
      return error;
    }
  };
}
export function updateInvoiceArraysWithFilesInRedux(invoiceFiles) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.updateInvoiceArraysWithFilesInRedux(invoiceFiles));
    } catch {
      /*Intentionally blank*/
    }
  };
}
export function updatePreviousInvoicingWithNewBookingVendorStatus(newStatus) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.updatePreviousInvoicingWithNewBookingVendorStatus(newStatus));
    } catch {
      /*Intentionally blank*/
    }
  };
}
export function getInvoicesInParsing() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getParsingInvoices();
      dispatch(slice.actions.getParsingInvoicesSuccess(response.data));
      return response;
    } catch (error) {
      return error;
    }
  };
}
export async function getInvoiceOCRInProgress(id) {
  try {
    const response = await getInvoiceOCRStatus(id);
    return response;
  } catch (error) {
    return error;
  }
}
export function stopInvoiceParse(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await stopParsingInvoice(id);
      if (response.status === 200) dispatch(slice.actions.stopParsingInvoiceSuccess(response.data));
      return response;
    } catch (error) {
      return error;
    }
  };
}
export function uploadInvoiceFiles(files) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await uploadManualInvoices(files);
      await dispatch(slice.actions.uploadManualInvoicesSuccess(response.data));
      return response;
    } catch (error) {
      console.log('error', error);
      return error;
    }
  };
}
export function invoiceForm({ invoice }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postInvoiceForm(invoice);
      return response.data;
    } catch (error) {
      return error;
    }
  };
}
export function removeFromQue(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await removeInvoiceFromQue(id);
      return response.data;
    } catch (error) {
      return error;
    }
  };
}
export function invoiceGLItemsForm({ invoiceGlItems, addedBy = '' }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postInvoiceGLItemsForm(invoiceGlItems, addedBy);
      return response.data;
    } catch (error) {
      return error;
    }
  };
}
export function deleteGlItems(invoiceId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await removeGlItems(invoiceId);
      await dispatch(slice.actions.removeGlItemsSuccess());
      return 'success';
    } catch (error) {
      return error;
    }
  };
}
export function facilityModeToggleChange(invoiceId, mode) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await facilityModeToggle(invoiceId, mode);
      return response.data;
    } catch (error) {
      return error;
    }
  };
}
export function getInvoices(editInProgress = null, invoiceIds = null) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getGroupedInvoicesData(editInProgress, invoiceIds);
      const { user } = getState().user;
      await dispatch(
        slice.actions.getInvoicesSuccess({
          data: response.data,
          editInProgress,
          invoiceIds,
          user,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getPendingInvoicing({ pending }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getPendingInvoicesData({ pending });
      dispatch(slice.actions.getPendingInvoicingSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getCompleteInvoices({ startDate, endDate }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getCompleteInvoicesData({ startDate, endDate });
      const invoices = response.data?.invoices?.map(x => ({ ...x, facility: response.data.facilities[x.facilityId]?.name, group: response.data.facilities[x.facilityId]?.group })) ?? [];
      dispatch(slice.actions.getCompleteInvoicesSuccess(invoices));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getFacilityManagers() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getFacilityManagersApi();
      dispatch(slice.actions.getFacilityManagersSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getStatementsInvoicing() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getInvoiceStatements({ pending: false });
      dispatch(slice.actions.getStatementsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getEditInvoiceFacilities(isManual) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getEditFacilities(isManual);
      dispatch(slice.actions.getEditInvoiceFacilitiesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getInvoiceDataFromRedux(isManual) {
  return async (dispatch) => {
    try {
      const response = await getInvoiceFormData(isManual);
      if (isManual) dispatch(slice.actions.getManualInvoiceDataSuccess(response.data));
      else dispatch(slice.actions.getInvoiceDataSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getInvoiceGls(invoiceId) {
  return async (dispatch) => {
    try {
      const response = await getInvoiceGlItems(invoiceId);
      await dispatch(slice.actions.getInvoiceGlsSuccess(response.data));
      return 'success';
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return 'error';
    }
  };
}
export function getInvoice(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getSingleInvoice(id);
      dispatch(slice.actions.getInvoiceSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getInvoiceFiles(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getInvoiceItemsFiles(id);
      dispatch(slice.actions.getInvoiceItemsFilesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getInvoiceFile(id, mainFile = true) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await downloadInvoiceFile(id, mainFile);
      dispatch(slice.actions.getInvoiceFileSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getInvoiceExcel(section, invoices, date, downloadType) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await downloadInvoicesExcel(section, invoices, date, downloadType);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function renderInvoiceFile(id, mainFile = true) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await viewInvoiceFile(id, mainFile);
      if (response?.data?.size === 0) {
        dispatch(slice.actions.renderInvoiceFileSuccess(response));
        return 'error';
      }
      dispatch(slice.actions.renderInvoiceFileSuccess(response));
      return 'success';
    } catch (error) {
      dispatch(slice.actions.renderInvoiceFileFail());
      return 'error';
    }
  };
}
export function addInvoiceFile(id, file) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postAddInvoiceFile(id, file);
      if (response.status !== 200) return 'error';
      dispatch(slice.actions.postAddInvoiceFileSuccess(response.data));
      return 'success';
    } catch (error) {
      return error;
    }
  };
}
export function getInvoiceBreakdown(id, mode = 'Full') {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getInvoiceBreakdownData(id, mode);
      await dispatch(slice.actions.getInvoiceBreakdownSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getInvoiceAnalyzeLogsData(id) {
  return async (dispatch) => {
    try {
      const response = await getInvoiceAnalyzeLogs(id);
      dispatch(slice.actions.getInvoiceAnalyzeLogsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getInvoiceAnalyzeLogsSuccess([]));
    }
  };
}
export function clearInvoiceAnalyzeLogs() {
  return (dispatch) => {
    dispatch(slice.actions.getInvoiceAnalyzeLogsSuccess([]));
  };
}
export function fetchDuplicateInvoices(input) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getDuplicateInvoices(input);
      await dispatch(slice.actions.getDuplicateInvoicesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function fetchRelatedInvoices(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getRelatedInvoices(id);
      await dispatch(slice.actions.getRelatedInvoicesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function changeInvoiceField(invoiceId, field) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await updateInvoiceField(invoiceId, field);
      dispatch(slice.actions.updateInvoiceFieldSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getGLs() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getInvoiceGLsData();

      dispatch(slice.actions.getGLsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getInvoiceLog(invoiceId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getInvoiceLogData(invoiceId);
      dispatch(slice.actions.getInvoiceLogSuccess(response.data));
      return 'success';
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
export function addInvoiceLog({
  invoiceId,
  messageType,
  extraInfo = '',
  mentions = null,
  parentCommentId = '',
  files = null,
  invoiceUrl,
}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await addInvoiceLogMessage(
        invoiceId,
        messageType,
        extraInfo,
        mentions,
        parentCommentId,
        files,
        invoiceUrl
      );
      dispatch(slice.actions.addInvoiceLogSuccess(response.data));
      return 'success';
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
export function deleteInvoiceLog(logId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await deleteInvoiceLogMessage(logId);
      dispatch(slice.actions.deleteInvoiceLogMessageSuccess(response.data));
      return 'success';
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
export function getInvoiceViewers(invoiceId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getInvoiceViewersList(invoiceId);
      dispatch(slice.actions.getInvoiceViewersListSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getInvoiceViewersListPerFacility(facilityId, sourceMethod) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getInvoiceViewersPerFacility(facilityId, sourceMethod);
      dispatch(slice.actions.getInvoiceViewersPerFacilitySuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getFacilityInvoiceSettings() {
  return async (dispatch) => {
    try {
      const response = await getFacilityInvoiceSettingsAPI();
      dispatch(slice.actions.getFacilityInvoiceSettingsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function submitForReview(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await requestReview(id);
      return 'success';
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
export function bulkSubmitInvoices(invoicesIds) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await bulkBookInvoices(invoicesIds);
      return  response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
//save approvers for specific invoice
export function saveApprovers(id, selectedApprovers) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postInvoiceApprovers(id, selectedApprovers);
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
export function getApprovers(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getInvoiceApprovers(id);
      dispatch(slice.actions.getInvoiceApproversSuccess(response.data));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
//Add approver for specific facility
export function saveFacilityApprovers({ facilityIds, vendorIds, approvers, isManual }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postFacilityInvoiceApprovers(facilityIds, vendorIds, approvers, isManual);
      if (response.status === 200) {
        dispatch(slice.actions.postFacilityInvoiceApproversSuccess(response.data));
        return 'success';
      }
      return 'error';
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
export function approveInvoice(invoiceId, approvalStatus = 'Approve', comment = '') {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await processInvoiceApproval(invoiceId, approvalStatus, comment);
      return response.data ? 'success' : 'error';
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
//Delete approvers for specific facility
export function deleteFacilityApprovers({ facilityIds, vendorIds, approvers, isManual }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postDeleteFacilityInvoiceApprovers(facilityIds, vendorIds, approvers, isManual);
      if (response.status === 200) {
        dispatch(slice.actions.postDeleteFacilityInvoiceApproversSuccess(response.data));
        return 'success';
      }
      return 'error';
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

//get all facilities approver options
export function getApproverOptions(isManual) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getInvoiceApproverOptions(isManual);
      await dispatch(slice.actions.getInvoiceApproverOptionsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

//get all facilities approvers
export function getFacilityInvoicingApprovers(isManual) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getFacilityApprovalAssignments(isManual);
      await dispatch(slice.actions.getFacilityApprovalAssignmentsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function patchFacilitiesGlConsolidation(facilities, isManual = false) {
  return async (dispatch) => {
    try {
      const response = await updateFacilitiesGlConsolidation(facilities, isManual);
      dispatch(slice.actions.updateFacilityInvoiceSettingsSuccess(response.data));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
export function updateFacilitySettingsInRedux(res) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.updateFacilityInvoiceSettingsSuccess(res.data));
      return res;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
export function removeInvoiceFromPending(id) {
  return async (dispatch) => {
    dispatch(slice.actions.removeFromPending(id));
  };
}
export function setPendingInvoiceCount(invoiceCount) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.getPendingInvoicesCountSuccess({ pendingCount: invoiceCount }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function addViewedInvoiceId(invoiceId) {
  return (dispatch) => {
    try {
      dispatch(slice.actions.addViewedInvoiceIdSuccess(invoiceId));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function clearViewedInvoiceIds() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.clearViewedInvoiceIdsSuccess());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getChatUsers(invoiceId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getInvoiceChatUsers(invoiceId);
      dispatch(slice.actions.setInvoiceChatUsers(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function addChatUser(invoiceId, userId, invoiceUrl, userEmail) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await addInvoiceChatUser(invoiceId, userId, invoiceUrl, userEmail);
      // if a non PS user was added, set email updates to true
      if (response?.data?.currentChatUsers?.some((user) => userEmail && user.email === userEmail && !user.id)) {
        dispatch(slice.actions.setEmailChatUpdatesVariable(true));
      }
      dispatch(slice.actions.setInvoiceChatUsers(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function deleteChatUser(invoiceId, userId, userEmail) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await deleteInvoiceChatUser(invoiceId, userId, userEmail);
      dispatch(slice.actions.setInvoiceChatUsers(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function updateEmailChatUpdates(invoiceId, sendUpdates) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await setEmailChatUpdates(invoiceId, sendUpdates);
      dispatch(slice.actions.setEmailChatUpdatesVariable(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function parseGls(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await parseGLItems(id);
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function getBookingSubmitSchedules() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getBookingTypeSubmitSchedules();
      dispatch(slice.actions.getBookingTypeSubmitSchedulesSuccess(response.data));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
//defaults to set only editable invoice vendors booking types
export function getVendorBookingTypes() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getInvoiceBookingTypes();
      dispatch(slice.actions.getBookingTypesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function deleteVendors(vendorIds) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postDeleteVendors(vendorIds);
      if (response.data.success) {
        dispatch(slice.actions.removeVendorsSuccess(vendorIds));
      }
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
export function getVendors(vendorIds = null) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getVendorsData(vendorIds);
      dispatch(slice.actions.getVendorsSuccess({ vendors: response.data, vendorIds }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
